import { useState } from 'react';
import { Button, Space, App } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { trpc } from '../../lib/api';
import { RejectReasonModal } from './RejectReasonModal';
import type { WorkScheduleDetail } from './DetailModal';
import { getMutationPendingState } from '../../utils/trpcMutation';

interface ApprovalButtonProps {
  schedule: WorkScheduleDetail;
  onApproved?: () => void;
  onRejected?: () => void;
  disabled?: boolean;
}

/**
 * 承認/差し戻しボタンコンポーネント
 * - 承認ボタン: 確認ダイアログ → 承認API呼び出し
 * - 差し戻しボタン: 理由入力モーダル → 差し戻しAPI呼び出し
 */
export function ApprovalButton({
  schedule,
  onApproved,
  onRejected,
  disabled = false
}: ApprovalButtonProps): JSX.Element {
  const { message } = App.useApp();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);

  const approveSchedule = trpc.workSchedule.approve.useMutation({
    onSuccess: () => {
      message.success('作業を承認しました');
      onApproved?.();
    },
    onError: (error) => {
      message.error(error.message || '承認に失敗しました');
    }
  });

  const rejectSchedule = trpc.workSchedule.reject.useMutation({
    onSuccess: () => {
      message.success('作業を差し戻しました');
      setRejectModalOpen(false);
      onRejected?.();
    },
    onError: (error) => {
      message.error(error.message || '差し戻しに失敗しました');
    }
  });

  const handleApprove = async () => {
    await approveSchedule.mutateAsync({
      id: schedule.id,
      comment: '承認しました'
    });
  };

  const handleReject = async (reason: string) => {
    await rejectSchedule.mutateAsync({
      id: schedule.id,
      reason
    });
  };

  const canApprove = schedule.status === 'pending_approval';

  if (!canApprove) {
    return <></>;
  }

  return (
    <>
      <Space>
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleApprove}
          loading={getMutationPendingState(approveSchedule)}
          disabled={disabled}
        >
          承認する
        </Button>
        <Button
          danger
          icon={<CloseCircleOutlined />}
          onClick={() => setRejectModalOpen(true)}
          loading={getMutationPendingState(rejectSchedule)}
          disabled={disabled}
        >
          差し戻す
        </Button>
      </Space>

      <RejectReasonModal
        open={rejectModalOpen}
        schedule={schedule}
        onSubmit={handleReject}
        onClose={() => setRejectModalOpen(false)}
        loading={getMutationPendingState(rejectSchedule)}
      />
    </>
  );
}
